/**
 * Valuation Progression Modal
 * Displays valuation scenarios matrix
 */
import React from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, X } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';

const ValuationProgressionModal = ({ show, onClose, valueGap, profitGap }) => {
  if (!show) return null;

  // Calculate valuation matrix for all combinations
  const calculateMatrix = () => {
    if (!valueGap || !profitGap) return null;

    const revenue = profitGap.current_revenue || 0;
    const currentEbitda = profitGap.current_ebitda || 1;
    const currentMargin = profitGap.current_ebitda_margin || 0;
    const medianMargin = profitGap.industry_median_ebitda_margin || 0;
    const bestMargin = profitGap.industry_high_ebitda_margin || 0;
    const ebitdaMultiple = profitGap.ebitda_multiple || 0;

    const currentScore = valueGap.current_attractiveness || 58;
    const greenZoneScore = 67;
    const bestScore = 72;

    // Get valuation range (these are dollar amounts at current EBITDA)
    const lowRange = valueGap.valuation_low || 0;
    const highRange = valueGap.valuation_high || 0;

    // Function to calculate valuation for a given EBITDA margin and assessment score
    // Uses the Value Gap framework: Position = (Score - 17) / 83 * 100
    // Valuation = Low + (Range × Position)
    const calculateValuation = (ebitdaMargin, assessmentScore) => {
      // Step 1: Calculate EBITDA at this margin level
      const newEbitda = revenue * (ebitdaMargin / 100);

      // Step 2: Calculate scaling factor to adjust valuation range for new EBITDA
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;

      // Step 3: Scale the valuation range proportionally
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      const newRange = newValHigh - newValLow;

      // Step 4: Calculate position within range based on assessment score
      // Formula: (Assessment Score - 17) / 83 × 100
      const position = Math.max(0, Math.min(100, (assessmentScore - 17) / 83 * 100));

      // Step 5: Calculate final valuation
      // Current Exit Value = Low + (Range × Position / 100)
      return newValLow + (newRange * position / 100);
    };

    // Calculate valuation ranges for each EBITDA margin scenario
    const calculateRangeForMargin = (ebitdaMargin) => {
      const newEbitda = revenue * (ebitdaMargin / 100);
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      return { low: newValLow, high: newValHigh };
    };

    const currentMarginRange = calculateRangeForMargin(currentMargin);
    const medianMarginRange = calculateRangeForMargin(medianMargin);
    const bestMarginRange = calculateRangeForMargin(bestMargin);

    // Calculate all 9 matrix cells (3 assessment scores × 3 EBITDA margins)
    return {
      // Row 1: Current Score at all three margins
      currentScore_currentMargin: calculateValuation(currentMargin, currentScore),
      currentScore_medianMargin: calculateValuation(medianMargin, currentScore),
      currentScore_bestMargin: calculateValuation(bestMargin, currentScore),
      // Row 2: Green Zone (67%) at all three margins
      greenZone_currentMargin: calculateValuation(currentMargin, greenZoneScore),
      greenZone_medianMargin: calculateValuation(medianMargin, greenZoneScore),
      greenZone_bestMargin: calculateValuation(bestMargin, greenZoneScore),
      // Row 3: Best In Class (72%) at all three margins
      bestScore_currentMargin: calculateValuation(currentMargin, bestScore),
      bestScore_medianMargin: calculateValuation(medianMargin, bestScore),
      bestScore_bestMargin: calculateValuation(bestMargin, bestScore),
      // Store the parameters for display
      currentScore,
      greenZoneScore,
      bestScore,
      currentMargin,
      medianMargin,
      bestMargin,
      ebitdaMultiple,
      // Store the valuation ranges
      currentMarginRange,
      medianMarginRange,
      bestMarginRange
    };
  };

  const matrix = calculateMatrix();

  if (!matrix) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl p-4 sm:p-6 lg:p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Your Valuation Journey</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
              <X size={24} className="sm:w-7 sm:h-7" />
            </button>
          </div>
          <p className="text-gray-600">Please calculate your Value Gap and Profit Gap first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header - Responsive */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <TrendingUp size={24} className="flex-shrink-0 sm:w-8 sm:h-8" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold truncate">Your Valuation Journey</h2>
              <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">Path to Exit Readiness</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
            aria-label="Close"
          >
            <X size={24} className="sm:w-7 sm:h-7" />
          </button>
        </div>

        {/* Matrix Content - Responsive padding */}
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Valuation Matrix */}
          {matrix && (
            <div>
              <h4 className="font-bold text-gray-900 mb-4 text-lg">Valuation Scenarios Matrix</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[500px]">
                  {/* Table Header */}
                  <thead>
                    <tr>
                      <th className="border-b-2 border-r-2 border-gray-800 bg-white p-2 sm:p-3" style={{ width: '180px' }}></th>
                      <th className="border-2 border-gray-800 bg-gray-100 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm" colSpan="3">
                        EBITDA Margin
                      </th>
                    </tr>
                    <tr>
                      <th className="border-2 border-gray-800 bg-gray-100 p-2 sm:p-3 text-right font-bold text-xs sm:text-sm" style={{ width: '180px' }}>
                        Assessment Score
                      </th>
                      <th className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                        Current
                      </th>
                      <th className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                        Median
                      </th>
                      <th className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                        Best In Class
                      </th>
                    </tr>
                  </thead>
                  {/* Table Body */}
                  <tbody>
                    {/* Row 1: Current Assessment Score at all three margins */}
                    <tr>
                      <td className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                        <div className="font-bold">Current</div>
                      </td>
                      <td className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.currentScore_currentMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.currentScore_medianMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.currentScore_bestMargin)}
                      </td>
                    </tr>
                    {/* Row 2: Green Zone (67%) at all three margins */}
                    <tr>
                      <td className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                        <div className="font-bold">Green Zone</div>
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.greenZone_currentMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.greenZone_medianMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.greenZone_bestMargin)}
                      </td>
                    </tr>
                    {/* Row 3: Best In Class (72%) at all three margins */}
                    <tr>
                      <td className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                        <div className="font-bold">Best In Class</div>
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.bestScore_currentMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.bestScore_medianMargin)}
                      </td>
                      <td className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                        {formatCurrency(matrix.bestScore_bestMargin)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Change from Baseline Matrix */}
              <div className="mt-8">
                <h4 className="font-bold text-gray-900 mb-4 text-lg">Change from Current Baseline</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse min-w-[500px]">
                    {/* Table Header */}
                    <thead>
                      <tr>
                        <th className="border-b-2 border-r-2 border-gray-800 bg-white p-2 sm:p-3" style={{ width: '180px' }}></th>
                        <th className="border-2 border-gray-800 bg-gray-100 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm" colSpan="3">
                          EBITDA Margin
                        </th>
                      </tr>
                      <tr>
                        <th className="border-2 border-gray-800 bg-gray-100 p-2 sm:p-3 text-right font-bold text-xs sm:text-sm" style={{ width: '180px' }}>
                          Assessment Score
                        </th>
                        <th className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                          Current
                        </th>
                        <th className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                          Median
                        </th>
                        <th className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">
                          Best In Class
                        </th>
                      </tr>
                    </thead>
                    {/* Table Body */}
                    <tbody>
                      {/* Row 1: Current Assessment Score at all three margins */}
                      <tr>
                        <td className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                          <div className="font-bold">Current</div>
                        </td>
                        <td className="border-2 border-gray-800 bg-gray-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>$0</div>
                          <div className="text-[10px] sm:text-xs text-gray-600">(0%)</div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.currentScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.currentScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.currentScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.currentScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                      </tr>
                      {/* Row 2: Green Zone (67%) at all three margins */}
                      <tr>
                        <td className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                          <div className="font-bold">Green Zone</div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.greenZone_currentMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.greenZone_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                        <td className="border-2 border-gray-800 bg-blue-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.greenZone_medianMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.greenZone_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.greenZone_bestMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.greenZone_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                      </tr>
                      {/* Row 3: Best In Class (72%) at all three margins */}
                      <tr>
                        <td className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-xs sm:text-sm text-right" style={{ width: '180px' }}>
                          <div className="font-bold">Best In Class</div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.bestScore_currentMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.bestScore_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                        <td className="border-2 border-gray-800 bg-white p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.bestScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.bestScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                        <td className="border-2 border-gray-800 bg-green-200 p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm">
                          <div>{formatCurrency(matrix.bestScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                          <div className="text-[10px] sm:text-xs text-emerald-600">
                            (+{(((matrix.bestScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  <p className="italic mb-4">
                    All changes are calculated relative to the Current/Current baseline value of {formatCurrency(matrix.currentScore_currentMargin)}
                  </p>
                </div>
              </div>

              {/* Matrix Parameters and Valuation Ranges */}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
                  {/* Matrix Parameters */}
                  <div className="text-lg text-gray-600">
                    <p className="mb-2 border-b-2 border-gray-800 pb-1"><span className="font-semibold">Matrix Parameters:</span></p>
                    <ul className="space-y-1 ml-4">
                      <li>• <span className="font-semibold">Industry EV/EBITDA Multiple:</span> {matrix.ebitdaMultiple.toFixed(2)}x</li>
                      <li>• <span className="font-semibold">Current EBITDA Margin:</span> {matrix.currentMargin.toFixed(1)}%</li>
                      <li>• <span className="font-semibold">Median Industry EBITDA Margin:</span> {matrix.medianMargin.toFixed(1)}%</li>
                      <li>• <span className="font-semibold">Best In Class Industry EBITDA Margin:</span> {matrix.bestMargin.toFixed(1)}%</li>
                      <li>• <span className="font-semibold">Current Overall Assessment Score:</span> {matrix.currentScore.toFixed(0)}%</li>
                      <li>• <span className="font-semibold">Green Zone Assessment Score:</span> {matrix.greenZoneScore}%</li>
                      <li>• <span className="font-semibold">Best In Class Assessment Score:</span> {matrix.bestScore}%</li>
                    </ul>
                  </div>

                  {/* Valuation Ranges */}
                  <div className="text-lg text-gray-600">
                    <p className="mb-2 border-b-2 border-gray-800 pb-1"><span className="font-semibold">Valuation Ranges by EBITDA Margin:</span></p>
                    <ul className="space-y-1 ml-4">
                      <li>
                        • <span className="font-semibold">Current Margin ({matrix.currentMargin.toFixed(1)}%):</span> {formatCurrency(matrix.currentMarginRange.low)} - {formatCurrency(matrix.currentMarginRange.high)}
                      </li>
                      <li>
                        • <span className="font-semibold">Median Margin ({matrix.medianMargin.toFixed(1)}%):</span> {formatCurrency(matrix.medianMarginRange.low)} - {formatCurrency(matrix.medianMarginRange.high)}
                      </li>
                      <li>
                        • <span className="font-semibold">Best In Class Margin ({matrix.bestMargin.toFixed(1)}%):</span> {formatCurrency(matrix.bestMarginRange.low)} - {formatCurrency(matrix.bestMarginRange.high)}
                      </li>
                    </ul>
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-500 italic">
                  Calculations use Value Gap framework: Position = (Score - 17) / 83 × 100; Valuation = Low + (Range × Position)
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

ValuationProgressionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  valueGap: PropTypes.shape({
    current_exit_value: PropTypes.number,
    valuation_low: PropTypes.number,
    valuation_high: PropTypes.number,
    current_attractiveness: PropTypes.number
  }),
  profitGap: PropTypes.shape({
    current_revenue: PropTypes.number,
    current_ebitda: PropTypes.number,
    current_ebitda_margin: PropTypes.number,
    target_ebitda: PropTypes.number,
    industry_median_ebitda_margin: PropTypes.number,
    industry_high_ebitda_margin: PropTypes.number,
    ebitda_multiple: PropTypes.number,
    private_company_discount: PropTypes.number,
    valuation_gap: PropTypes.number
  })
};

export default ValuationProgressionModal;
